import { useCurrentUser, useUserRoutes } from '@cosuno/authentication-frontend';
import { MainNavigation, PremiumIcon, useQueryMatches } from '@cosuno/cosuno-ui';
import { isCompanyFeatureEnabled, useExperimentFlags } from '@cosuno/feature-flags-frontend';
import { getImageSrcSet, getImageUrl, IMAGE_TYPE } from '@cosuno/form';
import { websiteRoutes } from '@cosuno/routes';
import React, { type ComponentProps, useCallback } from 'react';

import { COMPANY_TYPE } from '~/__gql__/graphql';
import { GoldIcon } from '~/components/GoldPackageOverview';
import { useUiState } from '~/shared/contexts/uiStateContext';
import useAnalytics from '~/shared/hooks/useAnalytics';
import useTranslation from '~/shared/hooks/useTranslation';
import { isAgentWithAccessToCompanyProfile } from '~/shared/utils/user';

interface NavbarProps {
  companyType: COMPANY_TYPE;
  canViewSubcontractors: boolean;
}

type NavigationItemProps = ComponentProps<typeof MainNavigation.Item>;
const CustomNavigationItem: React.FC<NavigationItemProps> = ({ onClick, ...props }) => {
  const { isMobile } = useQueryMatches();
  const { setIsMainNavigationCollapsed } = useUiState();
  const onClickHandler = useCallback<NonNullable<NavigationItemProps['onClick']>>(
    (event) => {
      if (isMobile) {
        setIsMainNavigationCollapsed(true);
      }
      onClick?.(event);
    },
    [isMobile, onClick, setIsMainNavigationCollapsed],
  );

  return <MainNavigation.Item {...props} onClick={onClickHandler} />;
};

const Navbar: React.FC<NavbarProps> = ({ companyType, canViewSubcontractors }) => {
  const { t } = useTranslation();

  const { trackEvent } = useAnalytics();

  const experimentFlags = useExperimentFlags();

  const routes = useUserRoutes();

  const { isMainNavigationCollapsed, setIsMainNavigationCollapsed } = useUiState();

  const user = useCurrentUser();
  if (!user) return null;

  const { company, language } = user;

  const customization = isCompanyFeatureEnabled(company, 'feature-custom-branding')
    ? {
        logoUrl: company.logoKey && getImageUrl(company.logoKey, IMAGE_TYPE.companyLogo),
        logoSrcSet: company.logoKey && getImageSrcSet(company.logoKey, IMAGE_TYPE.companyLogo),
        backgroundColor: company.primaryColor,
        textColor: company.contrastColor,
      }
    : undefined;
  const isInternalDocumentManagementEnabled = isCompanyFeatureEnabled(
    user?.company,
    'feature-internal-document-management',
  );

  return (
    <MainNavigation.Container
      data-cy-main-navbar
      mainUrl={routes.root()}
      isCollapsed={isMainNavigationCollapsed}
      setIsCollapsed={setIsMainNavigationCollapsed}
      collapseButtonLabel={t('mainNavigation.collapse')}
      expandButtonLabel={t('mainNavigation.expand')}
      customLogoUrl={customization?.logoUrl || undefined}
      customLogoSrcSet={customization?.logoSrcSet || undefined}
      customBackgroundColor={customization?.backgroundColor || undefined}
      customTextColor={customization?.textColor || undefined}
    >
      {companyType === COMPANY_TYPE.principal && (
        <>
          {!user.isNavatticEnabled && (
            <CustomNavigationItem
              url={routes.companyOnboarding()}
              iconType="rocket-launch"
              label={t('companyOnboarding:gettingStarted')}
              onClick={() => trackEvent('sidebarOnboarding')}
            />
          )}
          <CustomNavigationItem
            url={routes.projectsRoot()}
            iconType="archive"
            label={t('projects:projects')}
            onClick={() => trackEvent('sidebarProjects')}
          />
          <CustomNavigationItem
            url={routes.bidPackagesRoot()}
            iconType="folder-opened"
            label={t('bidPackages:title')}
            onClick={() => trackEvent('sidebarBidPackages')}
          />
          <CustomNavigationItem
            url={routes.bids()}
            iconType="file-text"
            label={t('bids:bids')}
            onClick={() => trackEvent('sidebarBids')}
          />
          <MainNavigation.Divider />
          {(isCompanyFeatureEnabled(company, 'feature-cosuno-network') ||
            canViewSubcontractors) && (
            <>
              <CustomNavigationItem
                url={routes.subcontractors()}
                iconType="people"
                label={t('subcontractors:subcontractors')}
                onClick={() => trackEvent('sidebarSubcontractors')}
              />
              <MainNavigation.Divider />
            </>
          )}
          {isCompanyFeatureEnabled(user.company, 'feature-task-management') && (
            <CustomNavigationItem
              url={routes.tasksRoot()}
              iconType="task"
              label={t('taskManagement:tasks')}
              onClick={() => trackEvent('sidebarTasks', { userType: user.type })}
            />
          )}
          {isCompanyFeatureEnabled(user.company, 'feature-addendums') && (
            <CustomNavigationItem
              url={routes.addendums()}
              iconType="file-plus"
              label={t('addendums:label')}
              onClick={() => trackEvent('sidebarAddenda')}
            />
          )}
          {isCompanyFeatureEnabled(user.company, 'feature-invoices') && (
            <CustomNavigationItem
              url={routes.invoices()}
              iconType="file-euro"
              label={t('billing:label')}
              onClick={() => trackEvent('sidebarBilling')}
            />
          )}
          {(isCompanyFeatureEnabled(user.company, 'feature-addendums') ||
            isCompanyFeatureEnabled(user.company, 'feature-invoices')) && (
            <MainNavigation.Divider />
          )}
          <CustomNavigationItem
            url={routes.internalDocuments()}
            iconType="hard-drive"
            label={t('pageTitles:internalDocuments')}
            onClick={() =>
              trackEvent('sidebarDocuments', {
                internalDocumentsEnabled: isInternalDocumentManagementEnabled,
              })
            }
          />
          {experimentFlags['experiment-gold-package-one-pager'] === 'Enabled' && (
            <CustomNavigationItem
              url={routes.goldPackageOverview()}
              label={t('goldPackageOverview:title')}
              renderIcon={() => <GoldIcon size={20} />}
              onClick={() => trackEvent('upsellGold')}
            />
          )}
          <CustomNavigationItem
            externalUrl={websiteRoutes.principalUserHelpUrl(language)}
            iconType="help"
            label={t('help')}
            onClick={() => trackEvent('sidebarHelpCenter', { userType: user.type })}
          />
        </>
      )}
      {companyType === COMPANY_TYPE.agent && (
        <>
          <CustomNavigationItem
            url={routes.agentCompanyOnboarding()}
            iconType="rocket-launch"
            label={t('companyOnboarding:gettingStarted')}
            onClick={() => trackEvent('sidebarOnboarding')}
          />
          <CustomNavigationItem
            url={routes.agentBidRequests()}
            iconType="archive"
            label={t('tenders')}
            onClick={() => trackEvent('sidebarBidRequests')}
          />
          <CustomNavigationItem
            url={routes.agentReverseMarketplace()}
            iconType="marketplace"
            label={t('pageTitles:marketplace')}
            onClick={() => trackEvent('sidebarReverseMarketplace')}
          />
          <CustomNavigationItem
            url={routes.agentCertificates()}
            iconType="file-text"
            label={t('common:certificates')}
            onClick={() => trackEvent('sidebarCertificates')}
          />
          {isCompanyFeatureEnabled(user.company, 'feature-task-management') && (
            <CustomNavigationItem
              url={routes.tasksRoot()}
              iconType="task"
              label={t('taskManagement:tasks')}
              onClick={() => trackEvent('sidebarTasks', { userType: user.type })}
            />
          )}
          {isAgentWithAccessToCompanyProfile(user) && (
            <CustomNavigationItem
              url={routes.company()}
              iconType="settings-outline"
              label={t('pageTitles:company')}
              onClick={() => trackEvent('sidebarMyCompany')}
            />
          )}
          <CustomNavigationItem
            url={routes.premium()}
            renderIcon={(size: number) => (
              <span>
                <PremiumIcon variant="light" size={size} />
              </span>
            )}
            label={t('pageTitles:cosunoPremium')}
            onClick={() => trackEvent('sidebarPremium')}
          />
          <MainNavigation.Divider />
          <CustomNavigationItem
            externalUrl={websiteRoutes.agentUserHelpUrl(language)}
            iconType="help"
            label={t('help')}
            onClick={() => trackEvent('sidebarHelpCenter', { userType: user.type })}
          />
        </>
      )}
    </MainNavigation.Container>
  );
};

export default Navbar;
